import gql from 'graphql-tag'
import toTitleCase from '../Functions/toTitleCase'

// Every relation option's label starts with its id, so it's unambiguous
// which record you're looking at even when the descriptive part is sparse.
export function decodeGlobalId(globalId) {
    try {
        const parts = atob(globalId).split(':')
        return parts.length === 2 ? parts[1] : globalId
    } catch (e) {
        return globalId
    }
}

function withIdPrefix(pk, descriptor) {
    return descriptor ? `#${pk} - ${descriptor}` : `#${pk}`
}

// resolveById always uses the model's dedicated single-object query (driver,
// car, incident, rep, carIssue, liabilityClaim, subrogationClaim, group), each
// taking the graphene global id -- exact, unambiguous, and cheap. Never the
// list queries: their free-text searchTerm is an OR'd substring match across
// many fields, so a pk like "1" can match dozens of unrelated records.

// --- Driver ---
const DriverOptionsQuery = gql`
    query DriverRelationOptions($searchTerm: String) {
        allDrivers(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name tlcLicense } }
        }
    }
`
const DriverByIdQuery = gql`
    query DriverById($id: ID) {
        driver(id: $id) { id pk name tlcLicense }
    }
`
function driverOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.tlcLicense ? `${node.name} (${node.tlcLicense})` : node.name) }
}

// --- Driver Tag ---
const DriverTagOptionsQuery = gql`
    query DriverTagRelationOptions($searchTerm: String) {
        allTags(searchTerm: $searchTerm, contentType: 13, first: 30, orderBy: ["name"]) {
            edges { node { id name } }
        }
    }
`
const TagByIdQuery = gql`
    query TagById($id: ID) {
        tag(id: $id) { id name }
    }
`
function tagOption(node) {
    return { value: node.id, label: node.name }
}

// --- Car ---
const CarOptionsQuery = gql`
    query CarRelationOptions($searchTerm: String) {
        allCars(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk year vin dmvPlate carModel { make name } } }
        }
    }
`
const CarByIdQuery = gql`
    query CarById($id: ID) {
        car(gqlId: $id) { id pk year vin dmvPlate carModel { make name } }
    }
`
function carOption(node) {
    const modelLabel = [node.year, node.carModel?.make, node.carModel?.name].filter(Boolean).join(' ')
    const idLabel = node.dmvPlate || node.vin || ''
    return { value: node.id, label: withIdPrefix(node.pk, [modelLabel, idLabel].filter(Boolean).join(' - ')) }
}

// --- User ---
const UserOptionsQuery = gql`
    query UserRelationOptions($searchTerm: String) {
        allReps(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name username email } }
        }
    }
`
const UserByIdQuery = gql`
    query UserById($id: ID!) {
        rep(id: $id) { id pk name username email }
    }
`
function userOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name || node.username || node.email) }
}

// --- Incident ---
const IncidentOptionsQuery = gql`
    query IncidentRelationOptions($searchTerm: String) {
        allIncidents(searchTerm: $searchTerm, first: 30, orderBy: ["-dateAdded"]) {
            edges { node { id pk title incidentType } }
        }
    }
`
const IncidentByIdQuery = gql`
    query IncidentById($id: ID!) {
        incident(incidentId: $id) { id pk title incidentType }
    }
`
function incidentOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.title || (node.incidentType ? `${node.incidentType} incident` : '')) }
}

// --- CarIssues ("Damage") ---
const CarIssuesOptionsQuery = gql`
    query CarIssuesRelationOptions($searchTerm: String) {
        allCarIssues(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk issueCategory { category } car { vin dmvPlate } } }
        }
    }
`
const CarIssueByIdQuery = gql`
    query CarIssueById($id: ID!) {
        carIssue(id: $id) { id pk issueCategory { category } car { vin dmvPlate } }
    }
`
function carIssueOption(node) {
    const idLabel = node.car?.dmvPlate || node.car?.vin || ''
    return { value: node.id, label: withIdPrefix(node.pk, [node.issueCategory?.category, idLabel].filter(Boolean).join(' - ')) }
}

// --- LiabilityClaim ---
const LiabilityClaimOptionsQuery = gql`
    query LiabilityClaimRelationOptions($searchTerm: String) {
        allLiabilityClaims(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk externalId incident { title } } }
        }
    }
`
const LiabilityClaimByIdQuery = gql`
    query LiabilityClaimById($id: ID!) {
        liabilityClaim(id: $id) { id pk externalId incident { title } }
    }
`
function liabilityClaimOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.externalId, node.incident?.title].filter(Boolean).join(' - ')) }
}

// --- SubrogationClaim ---
const SubrogationClaimOptionsQuery = gql`
    query SubrogationClaimRelationOptions($searchTerm: String) {
        allSubrogationClaims(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk externalId incident { title } } }
        }
    }
`
const SubrogationClaimByIdQuery = gql`
    query SubrogationClaimById($id: ID!) {
        subrogationClaim(id: $id) { id pk externalId incident { title } }
    }
`
function subrogationClaimOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.externalId, node.incident?.title].filter(Boolean).join(' - ')) }
}

// --- Branch ---
// allBranches has no free-text searchTerm; like Group it exposes its model's
// filter_fields, so search by the name icontains filter.
const BranchOptionsQuery = gql`
    query BranchRelationOptions($searchTerm: String) {
        allBranches(name_Icontains: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name } }
        }
    }
`
const BranchByIdQuery = gql`
    query BranchById($id: ID) {
        branch(id: $id) { id pk name }
    }
`
function branchOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- Line of Business ---
const LineOfBusinessOptionsQuery = gql`
    query LineOfBusinessRelationOptions($searchTerm: String) {
        allLineOfBusiness(name_Icontains: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const LineOfBusinessByIdQuery = gql`
    query LineOfBusinessById($id: ID!) {
        lineOfBusiness(id: $id) { id name }
    }
`
function lineOfBusinessOption(node) {
    return { value: node.id, label: withIdPrefix(decodeGlobalId(node.id), node.name) }
}


// --- Group ---
const GroupOptionsQuery = gql`
    query GroupRelationOptions($searchTerm: String) {
        allGroups(name_Icontains: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const GroupByIdQuery = gql`
    query GroupById($id: ID!) {
        group(id: $id) { id name }
    }
`
function groupOption(node) {
    return { value: node.id, label: withIdPrefix(decodeGlobalId(node.id), node.name) }
}

// --- Board ---
// Task.board is an FK, so the generic engine advertises `board` with
// relationModel "Board". Without an entry here the popover falls back to a raw
// pk text box -- and boards were the one legacy filter that offered a
// multi-select of titles, so users would have had to know a numeric pk.
// allBoards has no free-text searchTerm; like Group and Branch it exposes its
// model's filter_fields, so search by the title icontains filter.
const BoardOptionsQuery = gql`
    query BoardRelationOptions($searchTerm: String) {
        allBoards(title_Icontains: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk title } }
        }
    }
`
const BoardByIdQuery = gql`
    query BoardById($id: ID!) {
        board(id: $id) { id pk title }
    }
`
function boardOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.title) }
}

// --- TPA ---
const TPAOptionsQuery = gql`
    query TPARelationOptions($searchTerm: String) {
        allTpas(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name } }
        }
    }
`
const TPAByIdQuery = gql`
    query TPAById($id: ID) {
        tpa(id: $id) { id pk name }
    }
`
function tpaOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- TPAAdjuster ---
const TPAAdjusterOptionsQuery = gql`
    query TPAAdjusterRelationOptions($searchTerm: String) {
        allTpaAdjusters(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name externalId } }
        }
    }
`
const TPAAdjusterByIdQuery = gql`
    query TPAAdjusterById($id: ID) {
        tpaAdjuster(id: $id) { id pk name externalId }
    }
`
function tpaAdjusterOption(node) {
    const descriptor = node.name || node.externalId || ''
    return { value: node.id, label: withIdPrefix(node.pk, descriptor) }
}

// --- CarModel ---
const CarModelOptionsQuery = gql`
    query CarModelRelationOptions($searchTerm: String) {
        allCarModels(name_Icontains: $searchTerm, first: 30, orderBy: ["make", "name"]) {
            edges { node { id pk name make series groupType { name } } }
        }
    }
`
const CarModelByIdQuery = gql`
    query CarModelById($id: ID!) {
        carModel(id: $id) { id pk name make series groupType { name } }
    }
`
function carModelOption(node) {
    const descriptor = [toTitleCase(node.make), toTitleCase(node.name)].filter(Boolean).join(' ')
        + (node.series ? ` (${node.series})` : '')
        + (node.groupType?.name ? ` | ${node.groupType.name}` : '')
    return { value: node.id, label: withIdPrefix(node.pk, descriptor) }
}

// --- CarLocation ---
const CarLocationOptionsQuery = gql`
    query CarLocationRelationOptions($searchTerm: String) {
        allCarLocations(searchTerm: $searchTerm, first: 30, orderBy: ["name"]) {
            edges { node { id pk name } }
        }
    }
`
const CarLocationByIdQuery = gql`
    query CarLocationById($id: ID!) {
        carLocation(id: $id) { id pk name }
    }
`
function carLocationOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- AgreementType ---
const AgreementTypeOptionsQuery = gql`
    query AgreementTypeRelationOptions($searchTerm: String) {
        allAgreementTypes(searchTerm: $searchTerm, first: 30, orderBy: ["name"]) {
            edges { node { id pk name } }
        }
    }
`
const AgreementTypeByIdQuery = gql`
    query AgreementTypeById($id: ID!) {
        agreementType(id: $id) { id pk name }
    }
`
function agreementTypeOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- SubscriptionType ---
const SubscriptionTypeOptionsQuery = gql`
    query SubscriptionTypeRelationOptions($searchTerm: String) {
        allSubscriptionTypes(searchTerm: $searchTerm, first: 30, orderBy: ["name"]) {
            edges { node { id pk name } }
        }
    }
`
const SubscriptionTypeByIdQuery = gql`
    query SubscriptionTypeById($id: ID!) {
        subscriptionType(id: $id) { id pk name }
    }
`
function subscriptionTypeOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

const PaymentMethodOptionsQuery = gql`
    query PaymentMethodRelationOptions {
        allPaymentMethods(first: 30) {
            edges { node { id name } }
        }
    }
`
const PaymentMethodByIdQuery = gql`
    query PaymentMethodById($id: ID!) {
        paymentMethod(id: $id) { id name }
    }
`
function paymentMethodOption(node) {
    return { value: node.id, label: node.name }
}

// --- Vendor ---
// Shared by CarIssues.vendor (Car Issues) and Job.vendor (Jobs) -- one entry
// covers both pages since relation_model is the plain model class name.
const VendorOptionsQuery = gql`
    query VendorRelationOptions($searchTerm: String) {
        allVendors(searchTerm: $searchTerm, first: 30, orderBy: ["name"]) {
            edges { node { id pk name address } }
        }
    }
`
const VendorByIdQuery = gql`
    query VendorById($id: ID) {
        vendor(id: $id) { id pk name address }
    }
`
function vendorOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.name, node.address].filter(Boolean).join(' - ')) }
}

// --- CarIssueCategory ---
const CarIssueCategoryOptionsQuery = gql`
    query CarIssueCategoryRelationOptions($searchTerm: String) {
        allCarIssueCategories(category_Icontains: $searchTerm, first: 30, orderBy: ["category"]) {
            edges { node { id pk category description } }
        }
    }
`
const CarIssueCategoryByIdQuery = gql`
    query CarIssueCategoryById($id: ID!) {
        carIssueCategory(id: $id) { id pk category description }
    }
`
function carIssueCategoryOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.description || node.category) }
}

// --- JobType ---
// allJobTypes has no searchTerm/filter support at all (no filter_fields on
// JobTypeNode) -- like the deleted MaintenanceListHeader's own AllJobTypesQuery,
// this just fetches the (small) full list and lets the popover's own text
// input narrow it client-side.
const JobTypeOptionsQuery = gql`
    query JobTypeRelationOptions {
        allJobTypes(first: 30) {
            edges { node { id pk name } }
        }
    }
`
const JobTypeByIdQuery = gql`
    query JobTypeById($id: ID) {
        jobType(id: $id) { id pk name }
    }
`
function jobTypeOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- IncidentDocumentType ---
// allIncidentDocumentTypes' searchTerm matches name + description. Deliberately no
// isActive argument: filtering is retrospective, and old incidents carry documents of
// types that have since been deactivated -- hiding those would make them unfindable.
const IncidentDocumentTypeOptionsQuery = gql`
    query IncidentDocumentTypeRelationOptions($searchTerm: String) {
        allIncidentDocumentTypes(searchTerm: $searchTerm, first: 30, orderBy: ["category", "name"]) {
            edges { node { id pk name category } }
        }
    }
`
const IncidentDocumentTypeByIdQuery = gql`
    query IncidentDocumentTypeById($id: ID!) {
        incidentDocumentType(id: $id) { id pk name category }
    }
`
function incidentDocumentTypeOption(node) {
    // Names repeat across the five categories, so the category disambiguates.
    const descriptor = node.category ? `${node.name} (${toTitleCase(node.category)})` : node.name
    return { value: node.id, label: withIdPrefix(node.pk, descriptor) }
}

// --- IncidentParty / IncidentPartyPeople / IncidentPartyVehicle ---
// Back the Incident Party, Party People and Party Vehicle filters on the incidents
// page. Every party row belongs to exactly one incident, so the same name or VIN on two
// incidents is two options -- the incident pk in the label tells them apart.
function withIncidentSuffix(descriptor, incident) {
    return [descriptor, incident?.pk ? `(Incident #${incident.pk})` : ''].filter(Boolean).join(' ')
}

const IncidentPartyOptionsQuery = gql`
    query IncidentPartyRelationOptions($searchTerm: String) {
        allIncidentParties(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk partyName incident { pk } } }
        }
    }
`
const IncidentPartyByIdQuery = gql`
    query IncidentPartyById($id: ID!) {
        incidentParty(id: $id) { id pk partyName incident { pk } }
    }
`
function incidentPartyOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, withIncidentSuffix(node.partyName, node.incident)) }
}

const IncidentPartyPeopleOptionsQuery = gql`
    query IncidentPartyPeopleRelationOptions($searchTerm: String) {
        allIncidentPartyPeople(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name incidentParty { partyName incident { pk } } } }
        }
    }
`
const IncidentPartyPeopleByIdQuery = gql`
    query IncidentPartyPeopleById($id: ID!) {
        incidentPartyPeople(id: $id) { id pk name incidentParty { partyName incident { pk } } }
    }
`
function incidentPartyPeopleOption(node) {
    const descriptor = [node.name, node.incidentParty?.partyName].filter(Boolean).join(' - ')
    return { value: node.id, label: withIdPrefix(node.pk, withIncidentSuffix(descriptor, node.incidentParty?.incident)) }
}

const IncidentPartyVehicleOptionsQuery = gql`
    query IncidentPartyVehicleRelationOptions($searchTerm: String) {
        allIncidentPartyVehicles(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk vin plateNumber incidentParty { incident { pk } } } }
        }
    }
`
const IncidentPartyVehicleByIdQuery = gql`
    query IncidentPartyVehicleById($id: ID!) {
        incidentPartyVehicle(id: $id) { id pk vin plateNumber incidentParty { incident { pk } } }
    }
`
function incidentPartyVehicleOption(node) {
    const descriptor = [node.vin, node.plateNumber].filter(Boolean).join(' - ')
    return { value: node.id, label: withIdPrefix(node.pk, withIncidentSuffix(descriptor, node.incidentParty?.incident)) }
}

// --- Automation Trigger ---
const AutomationTriggerOptionsQuery = gql`
    query AutomationTriggerRelationOptions($searchTerm: String) {
        allTriggers(searchTerm: $searchTerm, first: 30) {
            edges {
                node {
                    id
                    triggerType
                }
            }
        }
    }
`
const AutomationTriggerByIdQuery = gql`
    query AutomationTriggerById($id: ID!) {
        trigger(id: $id) {
            id
            triggerType
        }
    }
`
function automationTriggerOption(node) {
    return { value: node.id, label: toTitleCase(node.triggerType) }
}

// --- Automation ---
const AutomationOptionsQuery = gql`
    query AutomationRelationOptions($searchTerm: String) {
        allAutomations(searchTerm: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const AutomationByIdQuery = gql`
    query AutomationById($id: ID!) {
        automation(id: $id) { id name }
    }
`
function automationOption(node) {
    return { value: node.id, label: toTitleCase(node.name) }
}

// Runs a query and maps a single-node result (`data.<queryFieldPath>`) into an
// option, or null if nothing came back.
function resolveSingular(apolloClient, query, id, fieldPath, mapNode) {
    return apolloClient.query({ query, variables: { id } }).then((result) => {
        const node = result.data?.[fieldPath]
        return node ? mapNode(node) : null
    })
}

export const RELATION_OPTIONS_REGISTRY = {
    Driver: {
        query: DriverOptionsQuery,
        getOptions: (data) => (data?.allDrivers?.edges || []).map(({ node }) => driverOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, DriverByIdQuery, id, 'driver', driverOption),
    },
    ColoredTag: {
        query: DriverTagOptionsQuery,
        getOptions: (data) => (data?.allTags?.edges || []).map(({ node }) => tagOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, TagByIdQuery, id, 'tag', tagOption),
    },
    Car: {
        query: CarOptionsQuery,
        getOptions: (data) => (data?.allCars?.edges || []).map(({ node }) => carOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, CarByIdQuery, id, 'car', carOption),
    },
    User: {
        query: UserOptionsQuery,
        getOptions: (data) => (data?.allReps?.edges || []).map(({ node }) => userOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, UserByIdQuery, id, 'rep', userOption),
    },
    Incident: {
        query: IncidentOptionsQuery,
        getOptions: (data) => (data?.allIncidents?.edges || []).map(({ node }) => incidentOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, IncidentByIdQuery, id, 'incident', incidentOption),
    },
    CarIssues: {
        query: CarIssuesOptionsQuery,
        getOptions: (data) => (data?.allCarIssues?.edges || []).map(({ node }) => carIssueOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, CarIssueByIdQuery, id, 'carIssue', carIssueOption),
    },
    LiabilityClaim: {
        query: LiabilityClaimOptionsQuery,
        getOptions: (data) => (data?.allLiabilityClaims?.edges || []).map(({ node }) => liabilityClaimOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, LiabilityClaimByIdQuery, id, 'liabilityClaim', liabilityClaimOption),
    },
    SubrogationClaim: {
        query: SubrogationClaimOptionsQuery,
        getOptions: (data) => (data?.allSubrogationClaims?.edges || []).map(({ node }) => subrogationClaimOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, SubrogationClaimByIdQuery, id, 'subrogationClaim', subrogationClaimOption),
    },
    Group: {
        query: GroupOptionsQuery,
        getOptions: (data) => (data?.allGroups?.edges || []).map(({ node }) => groupOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, GroupByIdQuery, id, 'group', groupOption),
    },
    Branch: {
        query: BranchOptionsQuery,
        getOptions: (data) => (data?.allBranches?.edges || []).map(({ node }) => branchOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, BranchByIdQuery, id, 'branch', branchOption),
    },
    Board: {
        query: BoardOptionsQuery,
        getOptions: (data) => (data?.allBoards?.edges || []).map(({ node }) => boardOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, BoardByIdQuery, id, 'board', boardOption),
    },
    LineOfBusiness: {
        query: LineOfBusinessOptionsQuery,
        getOptions: (data) => (data?.allLineOfBusiness?.edges || []).map(({ node }) => lineOfBusinessOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, LineOfBusinessByIdQuery, id, 'lineOfBusiness', lineOfBusinessOption),
    },
    TPA: {
        query: TPAOptionsQuery,
        getOptions: (data) => (data?.allTpas?.edges || []).map(({ node }) => tpaOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, TPAByIdQuery, id, 'tpa', tpaOption),
    },
    TPAAdjuster: {
        query: TPAAdjusterOptionsQuery,
        getOptions: (data) => (data?.allTpaAdjusters?.edges || []).map(({ node }) => tpaAdjusterOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, TPAAdjusterByIdQuery, id, 'tpaAdjuster', tpaAdjusterOption),
    },
    CarModel: {
        query: CarModelOptionsQuery,
        getOptions: (data) => (data?.allCarModels?.edges || []).map(({ node }) => carModelOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, CarModelByIdQuery, id, 'carModel', carModelOption),
    },
    CarLocation: {
        query: CarLocationOptionsQuery,
        getOptions: (data) => (data?.allCarLocations?.edges || []).map(({ node }) => carLocationOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, CarLocationByIdQuery, id, 'carLocation', carLocationOption),
    },
    AgreementType: {
        query: AgreementTypeOptionsQuery,
        getOptions: (data) => (data?.allAgreementTypes?.edges || []).map(({ node }) => agreementTypeOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, AgreementTypeByIdQuery, id, 'agreementType', agreementTypeOption),
    },
    SubscriptionType: {
        query: SubscriptionTypeOptionsQuery,
        getOptions: (data) => (data?.allSubscriptionTypes?.edges || []).map(({ node }) => subscriptionTypeOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, SubscriptionTypeByIdQuery, id, 'subscriptionType', subscriptionTypeOption),
    },
    Trigger: {
        query: AutomationTriggerOptionsQuery,
        getOptions: (data) => (data?.allTriggers?.edges || []).map(({ node }) => automationTriggerOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, AutomationTriggerByIdQuery, id, 'trigger', automationTriggerOption),
    },
    Automation: {
        query: AutomationOptionsQuery,
        getOptions: (data) => (data?.allAutomations?.edges || []).map(({ node }) => automationOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, AutomationByIdQuery, id, 'automation', automationOption),
    },
    Vendor: {
        query: VendorOptionsQuery,
        getOptions: (data) => (data?.allVendors?.edges || []).map(({ node }) => vendorOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, VendorByIdQuery, id, 'vendor', vendorOption),
    },
    PaymentMethod: {
        query: PaymentMethodOptionsQuery,
        getOptions: (data) => (data?.allPaymentMethods?.edges || []).map(({ node }) => paymentMethodOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, PaymentMethodByIdQuery, id, 'paymentMethod', paymentMethodOption),
    },
    CarIssueCategory: {
        query: CarIssueCategoryOptionsQuery,
        getOptions: (data) => (data?.allCarIssueCategories?.edges || []).map(({ node }) => carIssueCategoryOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, CarIssueCategoryByIdQuery, id, 'carIssueCategory', carIssueCategoryOption),
    },
    JobType: {
        query: JobTypeOptionsQuery,
        getOptions: (data) => (data?.allJobTypes?.edges || []).map(({ node }) => jobTypeOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, JobTypeByIdQuery, id, 'jobType', jobTypeOption),
    },
    IncidentDocumentType: {
        query: IncidentDocumentTypeOptionsQuery,
        getOptions: (data) =>
            (data?.allIncidentDocumentTypes?.edges || []).map(({ node }) => incidentDocumentTypeOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, IncidentDocumentTypeByIdQuery, id, 'incidentDocumentType', incidentDocumentTypeOption),
    },
    IncidentParty: {
        query: IncidentPartyOptionsQuery,
        getOptions: (data) => (data?.allIncidentParties?.edges || []).map(({ node }) => incidentPartyOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, IncidentPartyByIdQuery, id, 'incidentParty', incidentPartyOption),
    },
    IncidentPartyPeople: {
        query: IncidentPartyPeopleOptionsQuery,
        getOptions: (data) =>
            (data?.allIncidentPartyPeople?.edges || []).map(({ node }) => incidentPartyPeopleOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, IncidentPartyPeopleByIdQuery, id, 'incidentPartyPeople', incidentPartyPeopleOption),
    },
    IncidentPartyVehicle: {
        query: IncidentPartyVehicleOptionsQuery,
        getOptions: (data) =>
            (data?.allIncidentPartyVehicles?.edges || []).map(({ node }) => incidentPartyVehicleOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, IncidentPartyVehicleByIdQuery, id, 'incidentPartyVehicle', incidentPartyVehicleOption),
    },
}
